/**
 * ③ Demande du code et ④ saisie.
 *
 * Regroupes : ce sont deux moments d'une meme etape, et les separer en
 * deux pages ferait perdre le code a qui revient en arriere.
 *
 * Le canal decoule de `auth_method`, fixe par le client a la creation de
 * l'enveloppe : il ne se choisit PAS ici. Un signataire configure en OTP
 * e-mail ne doit pas pouvoir basculer en SMS payant.
 */
import { useEffect, useRef, useState } from "react";

import { ApiError, requestOtp, verifyOtp, type SignerView } from "../api";
import type { t } from "../i18n";
import { ErrorPanel } from "../components/ErrorPanel";
import { CleAcces } from "./CleAcces";

interface Props {
  token: string;
  view: SignerView;
  L: ReturnType<typeof t>;
  onSigned: () => void;
  onDecline: () => void;
  /** B-28 : le gros bouton « je valide, je signe et je recois mon code »
   *  vient d'etre presse - le code part tout de suite, sans second clic. */
  envoiImmediat?: boolean;
}

/** OTP_TTL_MINUTES cote backend. Le compte a rebours est indicatif : c'est
 *  le serveur qui tranche, jamais l'horloge du navigateur. */
const TTL_SECONDS = 10 * 60;
/** Delai avant de pouvoir redemander un code : evite qu'un signataire
 *  impatient epuise ses 5 demandes (MAX_OTP_PER_SIGNER) en dix secondes. */
const RESEND_COOLDOWN_S = 30;

export function Otp(props: Props) {
  // 15/10/2026 : en signature avancée par clé d'accès, la clé remplace le code
  // (le serveur refuse alors toute demande de code). Composant à part : un
  // retour anticipé avant les hooks de la saisie du code serait interdit.
  if (props.view.cle_acces) {
    return <CleAcces token={props.token} transmettre={props.L.transmettre} onSigned={props.onSigned} onDecline={props.onDecline}
                     refuser={props.L.refuse} />;
  }
  return <SaisieDuCode {...props} />;
}

function SaisieDuCode({ token, view, L, onSigned, onDecline, envoiImmediat }: Props) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const isSms = view.otp_channel === "sms";

  // Compte a rebours de validite et delai de renvoi, sur un seul
  // intervalle - deux minuteries separees derivent l'une de l'autre.
  useEffect(() => {
    if (!sent) return;
    const id = window.setInterval(() => {
      setRemaining((s) => (s > 0 ? s - 1 : 0));
      setCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [sent]);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await requestOtp(token);
      setSent(true);
      setRemaining(TTL_SECONDS);
      setCooldown(RESEND_COOLDOWN_S);
      setCode("");
      // Mise au point sur le champ apres l'envoi : sur mobile cela ouvre
      // le clavier numerique et permet le remplissage automatique du SMS.
      window.setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError("server", 0, null));
    } finally {
      setSending(false);
    }
  };

  // Une seule fois, meme si React rejoue l'effet (mode strict) : deux
  // demandes feraient partir deux codes et entameraient le quota de renvoi.
  const dejaEnvoye = useRef(false);
  useEffect(() => {
    if (!envoiImmediat || dejaEnvoye.current) return;
    dejaEnvoye.current = true;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envoiImmediat]);

  const verify = async () => {
    setVerifying(true);
    setError(null);
    try {
      await verifyOtp(token, code.trim());
      onSigned();
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError("server", 0, null);
      setError(err);
      if (err.kind === "invalid_code") {
        setCode("");
        inputRef.current?.focus();
      }
    } finally {
      setVerifying(false);
    }
  };

  const mmss = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;

  // Signataire configure en SMS sans numero : la demande de code
  // echouerait avec une erreur opaque. On le dit avant, et on oriente vers
  // l'emetteur - le destinataire ne peut rien corriger lui-meme.
  if (view.otp_destination_missing) {
    return (
      <section className="card stack">
        <h1 className="card__title">{L.otpTitle}</h1>
        <div className="alert alert--error" role="alert">
          {L.errors.channel_unavailable}
        </div>
        <div className="actions">
          <button type="button" className="btn btn--danger" onClick={onDecline}>
            {L.refuse}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="card stack">
      <div>
        <h1 className="card__title">{L.otpTitle}</h1>
        <p className="card__lead">{isSms ? L.otpIntroSms : L.otpIntroEmail}</p>
        {/* Coordonnee MASQUEE : permet de verifier qu'on ecrit au bon
            endroit sans la divulguer a quelqu'un qui aurait intercepte le
            lien. */}
        <p style={{ fontWeight: 600, fontSize: "1.1rem" }}>{view.otp_destination_masked}</p>
      </div>

      {/* L'erreur est affichee ICI, hors du bloc "code envoye".
          Trouve en testant reellement le 29/08/2026 : quand la TOUTE
          PREMIERE demande echoue (canal indisponible -> 502), `sent`
          reste false, et l'erreur - qui n'etait rendue que dans la
          branche "envoye" - restait invisible. Le signataire cliquait,
          rien ne se passait, et il restait bloque sans explication : le
          pire des scenarios pour un ecran dont il ne peut de toute facon
          rien corriger lui-meme. */}
      {error && !sent && <ErrorPanel error={error} L={L} />}

      {!sent ? (
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void send()}
            disabled={sending}
          >
            {L.otpSend}
          </button>
        </div>
      ) : (
        <>
          <p className="alert alert--info" role="status">
            {isSms ? L.otpSentSms : L.otpSentEmail}
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Refermer le pave numerique AVANT de verifier (B-20,
              // 22/09/2026). Sans ce `blur`, valider depuis la touche du
              // clavier laisse le pave ouvert : il couvre le bas de
              // l'ecran, donc le bouton, le compte a rebours et le
              // message d'erreur que la verification va peut-etre
              // afficher. Le signataire voit alors son geste ne rien
              // produire.
              //
              // Le `focus()` de `verify()` sur code invalide rouvre le
              // clavier a dessein : la, on veut qu'il ressaisisse.
              inputRef.current?.blur();
              void verify();
            }}
          >
            <label className="field-label" htmlFor="otp-code">
              {L.codeLabel}
            </label>
            <input
              id="otp-code"
              ref={inputRef}
              className="code-input"
              value={code}
              onChange={(e) => {
                const saisi = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(saisi);
                // ⚠️ iOS N'A AUCUNE TOUCHE DE VALIDATION sur le pave
                // numerique (mesure le 22/09/2026, iPhone, iOS 26.6) : avec
                // `inputMode="numeric"` le clavier affiche dix chiffres et
                // rien d'autre - ni « Entree », ni « OK ». `enterKeyHint`
                // n'y change rien, il nomme une touche qui n'existe pas.
                // Le signataire n'a donc AUCUN moyen de refermer le pave
                // depuis le clavier, et le pave couvre le bouton de
                // validation qui vient tout juste de s'activer.
                //
                // On referme donc nous-memes au sixieme chiffre. Le bouton
                // apparait, et le signataire le touche.
                //
                // Pourquoi PAS un envoi automatique au sixieme chiffre,
                // qui reglerait aussi le probleme : la validation du code
                // EST l'acte de signature (cf. l'ecran ②bis). Elle doit
                // rester un geste delibere, pas la consequence mecanique
                // d'avoir fini de taper.
                if (saisi.length === 6 && code.length < 6) {
                  e.target.blur();
                }
              }}
              // inputMode numeric : clavier chiffres sur mobile.
              inputMode="numeric"
              // enterKeyHint done : la touche d'action du pave porte
              // « OK / Terminé » plutot que la fleche grise par defaut,
              // qui ne dit pas ce qu'elle fait. Android l'honore depuis
              // longtemps, iOS depuis 13.4 ; ailleurs c'est ignore sans
              // effet de bord.
              enterKeyHint="done"
              // one-time-code : iOS et Android proposent alors le code
              // directement depuis le SMS recu. Detail minuscule, gain de
              // conversion reel.
              autoComplete="one-time-code"
              // Le code n'a rien a faire dans un correcteur ou une
              // capitalisation automatique.
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              // Porteur, 12/10/2026 : un code colle avec ses espaces (« 3 7 2 0 1 3 »)
              // etait coupe a 6 caracteres AVANT le nettoyage. Plus de maxLength :
              // onChange ne garde que les 6 premiers chiffres ; au collage, idem.
              onPaste={(e) => {
                e.preventDefault();
                setCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
              }}
              aria-invalid={error?.kind === "invalid_code" ? "true" : undefined}
              aria-describedby="otp-help"
            />
            <p id="otp-help" className="muted">
              {L.codeHelp}{" "}
              {remaining > 0 ? L.codeExpiresIn(mmss) : <strong>{L.codeExpired}</strong>}
            </p>

            {error && <ErrorPanel error={error} L={L} />}

            <div className="actions">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={code.length !== 6 || verifying || remaining === 0}
              >
                {L.codeSubmit}
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void send()}
                disabled={sending || cooldown > 0}
              >
                {cooldown > 0 ? L.otpResendIn(cooldown) : L.otpResend}
              </button>
            </div>
          </form>
        </>
      )}

      <div className="actions">
        <button type="button" className="btn btn--danger" onClick={onDecline}>
          {L.refuse}
        </button>
      </div>
    </section>
  );
}
